/**
 * TypeScript types for Authentication Methods Configuration
 * Matches backend Pydantic models in backend/app/models/authentication_methods.py
 */

/**
 * Request model for updating authentication method configuration
 */
export interface AuthenticationMethodConfigRequest {
  state: 'enabled' | 'disabled';
  configuration: Record<string, unknown>;
}

/**
 * Response model for authentication method configuration
 */
export interface AuthenticationMethodConfigResponse {
  methodKey: string;
  state: 'enabled' | 'disabled';
  configuration: Record<string, unknown>;
}

/**
 * Authentication method configuration with method-specific settings
 */
export interface AuthenticationMethodConfig {
  methodKey: string;
  state: 'enabled' | 'disabled';
  configuration: Record<string, unknown>;
}

/**
 * Method IDs mapping for all supported authentication methods
 */
export const AUTHENTICATION_METHODS = {
  sms: 'SMS',
  email: 'Email OTP',
  fido2: 'FIDO2 Security Keys',
  voice: 'Voice Call',
  software_oath: 'Software OATH Tokens',
  hardware_oath: 'Hardware OATH Tokens',
  x509_certificate: 'X.509 Certificate',
  microsoft_authenticator: 'Microsoft Authenticator',
  temporary_access_pass: 'Temporary Access Pass',
  windows_hello: 'Windows Hello',
} as const;

/**
 * Type-safe authentication method keys
 */
export type AuthenticationMethodKey = keyof typeof AUTHENTICATION_METHODS;

/**
 * Type-safe authentication method display names
 */
export type AuthenticationMethodName = typeof AUTHENTICATION_METHODS[AuthenticationMethodKey];

/**
 * Authentication method types from Microsoft Graph API
 *
 * These correspond to the @odata.type values returned from the
 * /users/{user_id}/authentication/methods endpoint.
 */
export type AuthMethodType =
  | '#microsoft.graph.phoneAuthenticationMethod' // Phone/SMS
  | '#microsoft.graph.emailAuthenticationMethod' // Email OTP
  | '#microsoft.graph.microsoftAuthenticatorAuthenticationMethod' // Microsoft Authenticator
  | '#microsoft.graph.fido2AuthenticationMethod' // FIDO2/Passkeys
  | '#microsoft.graph.temporaryAccessPassAuthenticationMethod' // TAP
  | '#microsoft.graph.softwareOathAuthenticationMethod' // Software OATH tokens
  | '#microsoft.graph.windowsHelloForBusinessAuthenticationMethod' // Windows Hello
  | '#microsoft.graph.x509CertificateAuthenticationMethod' // X.509 Certificate
  | '#microsoft.graph.hardwareOathAuthenticationMethod'; // Hardware OATH tokens

/**
 * Individual authentication method from Graph API
 */
export interface AuthMethod {
  '@odata.type': AuthMethodType;
  id: string;
  [key: string]: unknown; // Additional properties vary by method type
}

/**
 * Response from batch authentication methods endpoint
 */
export interface BatchAuthMethodsResponse {
  methods: {
    [userId: string]: AuthMethod[];
  };
}
